import React, { useState, useEffect } from 'react';
import axiosConfig from "../../api/axiosConfig";
import { FiDownload, FiUpload, FiSave, FiRefreshCw, FiPlus, FiTrash2, FiDroplet, FiCheck } from 'react-icons/fi';
import * as XLSX from 'xlsx';

const TodoListPage = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [cellColors, setCellColors] = useState({});
  const [selectedCells, setSelectedCells] = useState([]);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  
  // Notification state
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null });

  // Renk paleti
  const colorPalette = [
    { name: 'Kırmızı', value: '#fee2e2', border: '#f87171' },
    { name: 'Turuncu', value: '#fed7aa', border: '#fb923c' },
    { name: 'Sarı', value: '#fef3c7', border: '#fbbf24' },
    { name: 'Yeşil', value: '#d1fae5', border: '#34d399' },
    { name: 'Mavi', value: '#dbeafe', border: '#60a5fa' },
    { name: 'Mor', value: '#e9d5ff', border: '#a78bfa' },
    { name: 'Pembe', value: '#fce7f3', border: '#f472b6' },
    { name: 'Gri', value: '#f3f4f6', border: '#9ca3af' },
  ];

  // Notification göster
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Confirm dialog göster
  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ show: true, message, onConfirm });
  };

  // Confirm dialog kapat
  const closeConfirm = (confirmed = false) => {
    if (confirmed && confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog({ show: false, message: '', onConfirm: null });
  };

  useEffect(() => {
    loadExcelData();
  }, []);

  // Dışarı tıklandığında paletleri kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Renk paleti container'ına tıklanmadıysa kapat
      if (!event.target.closest('.color-palette-container') && 
          !event.target.closest('.color-button')) {
        setShowColorPalette(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Excel verilerini yükle
  const loadExcelData = async () => {
    try {
      setLoading(true);
      const response = await axiosConfig.get('/admin/todos/data', {
        responseType: 'arraybuffer'
      });
      
      const workbook = XLSX.read(response.data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Worksheet boş olabilir
      if (!worksheet || !worksheet['!ref']) {
        console.log('Boş worksheet');
        setColumns([]);
        setRows([]);
        setHasChanges(false);
        setLoading(false);
        return;
      }
      
      // Range'i güvenli bir şekilde al
      let range;
      try {
        range = XLSX.utils.decode_range(worksheet['!ref']);
      } catch (e) {
        console.error('Range decode hatası:', e);
        setColumns([]);
        setRows([]);
        setHasChanges(false);
        setLoading(false);
        return;
      }
      
      // Başlıkları al
      const headers = [];
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: 0 })];
        if (cell && cell.v) {
          headers.push({
            key: `col_${C}`,
            name: cell.v.toString(),
            width: '150px'
          });
        }
      }
      
      // Eğer başlık yoksa boş tablo
      if (headers.length === 0) {
        setColumns([]);
        setRows([]);
        setHasChanges(false);
        setLoading(false);
        return;
      }
      
      setColumns(headers);
      
      // Veriyi oku
      const formattedData = [];
      const colors = {};
      
      // İlk satır başlık olduğu için 1'den başla
      for (let R = 1; R <= range.e.r; ++R) {
        const row = { _id: R - 1 };
        let hasData = false;
        
        headers.forEach((header, index) => {
          const cellAddress = XLSX.utils.encode_cell({ c: index, r: R });
          const cell = worksheet[cellAddress];
          const value = cell ? (cell.v || '') : '';
          row[header.key] = value.toString();
          if (value) hasData = true;
          
          // Hücre rengini oku (eğer varsa)
          if (cell && cell.s && cell.s.fgColor && cell.s.fgColor.rgb) {
            const cellId = `${R-1}_${header.key}`;
            colors[cellId] = `#${cell.s.fgColor.rgb.slice(2)}`;
          }
        });
        
        // En az bir hücrede veri varsa satırı ekle
        if (hasData) {
          formattedData.push(row);
        }
      }
      
      setRows(formattedData);
      setCellColors(colors);
      setHasChanges(false);
      console.log('Excel başarıyla yüklendi:', { headers: headers.length, rows: formattedData.length });
    } catch (error) {
      console.error('Excel yükleme hatası:', error);
      // Hata durumunda boş tablo göster
      setColumns([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Hücre değişikliği
  const handleCellChange = (rowId, field, value) => {
    setRows(rows.map(row => 
      row._id === rowId ? { ...row, [field]: value } : row
    ));
    setHasChanges(true);
  };

  // Hücre seçimi
  const handleCellClick = (rowId, field, event) => {
    const cellId = `${rowId}_${field}`;
    
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd tuşu ile çoklu seçim
      if (selectedCells.includes(cellId)) {
        setSelectedCells(selectedCells.filter(id => id !== cellId));
      } else {
        setSelectedCells([...selectedCells, cellId]);
      }
    } else {
      // Tek seçim
      setSelectedCells([cellId]);
    }
  };

  // Renk uygula
  const applyColor = (color) => {
    if (selectedCells.length === 0) return;
    
    const newColors = { ...cellColors };
    selectedCells.forEach(cellId => {
      if (color) {
        newColors[cellId] = color.value;
      } else {
        delete newColors[cellId];
      }
    });
    
    setCellColors(newColors);
    setHasChanges(true);
    setShowColorPalette(false);
    setSelectedColor(color);
  };

  // Yeni satır ekle
  const handleAddRow = () => {
    if (columns.length === 0) {
      showNotification('warning', 'Önce en az bir sütun eklemelisiniz!');
      return;
    }
    
    const newRow = {
      _id: rows.length
    };
    
    // Her sütun için boş değer ekle
    columns.forEach(col => {
      newRow[col.key] = '';
    });
    
    setRows([...rows, newRow]);
    setHasChanges(true);
  };

  // Yeni sütun ekle
  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      showNotification('error', 'Lütfen sütun adı girin!');
      return;
    }
    
    const newColumn = {
      key: `col_${Date.now()}`,
      name: newColumnName.trim(),
      width: '150px'
    };
    
    setColumns([...columns, newColumn]);
    
    // Mevcut satırlara bu sütunu ekle
    setRows(rows.map(row => ({
      ...row,
      [newColumn.key]: ''
    })));
    
    setNewColumnName('');
    setShowColumnModal(false);
    setHasChanges(true);
    showNotification('success', 'Sütun başarıyla eklendi!');
  };

  // Sütun sil
  const handleDeleteColumn = (columnKey) => {
    if (columns.length === 1) {
      showNotification('error', 'En az bir sütun olmalıdır!');
      return;
    }
    
    showConfirm('Bu sütunu silmek istediğinize emin misiniz?', () => {
      setColumns(columns.filter(col => col.key !== columnKey));
      
      // Satırlardan bu sütunu kaldır
      setRows(rows.map(row => {
        const newRow = { ...row };
        delete newRow[columnKey];
        return newRow;
      }));
      
      // Bu sütundaki renkleri de sil
      const newColors = { ...cellColors };
      Object.keys(newColors).forEach(cellId => {
        if (cellId.endsWith(`_${columnKey}`)) {
          delete newColors[cellId];
        }
      });
      setCellColors(newColors);
      
      setHasChanges(true);
      showNotification('success', 'Sütun başarıyla silindi!');
    });
  };

  // Seçili satırları sil
  const handleDeleteRows = () => {
    if (selectedRows.length === 0) return;
    
    const count = selectedRows.length;
    showConfirm(`${count} satırı silmek istediğinize emin misiniz?`, () => {
      setRows(rows.filter(row => !selectedRows.includes(row._id)));
      
      // Silinen satırların renklerini de kaldır
      const newColors = { ...cellColors };
      selectedRows.forEach(rowId => {
        columns.forEach(col => {
          delete newColors[`${rowId}_${col.key}`];
        });
      });
      setCellColors(newColors);
      
      setSelectedRows([]);
      setHasChanges(true);
      showNotification('success', `${count} satır başarıyla silindi!`);
    });
  };

  // Değişiklikleri kaydet
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // En az bir sütun olmalı
      if (columns.length === 0) {
        showNotification('warning', 'Kaydetmek için en az bir sütun olmalıdır!');
        return;
      }
      
      // Excel formatına dönüştür
      const worksheetData = [];
      
      // Başlıkları ekle
      const headers = columns.map(col => col.name);
      worksheetData.push(headers);
      
      // Veriyi hazırla
      rows.forEach(row => {
        const rowData = [];
        columns.forEach(col => {
          rowData.push(row[col.key] || '');
        });
        worksheetData.push(rowData);
      });
      
      // Excel oluştur
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      
      // Renkleri uygula (basit renklendirme için)
      // Not: XLSX-js renk desteği sınırlı olduğu için renkler kaybolabilir
      
      XLSX.utils.book_append_sheet(wb, ws, 'Yapılacaklar');
      
      // Worksheet'in ref'ini ayarla
      if (!ws['!ref'] && worksheetData.length > 0) {
        const maxCol = Math.max(...worksheetData.map(row => row.length)) - 1;
        const maxRow = worksheetData.length - 1;
        ws['!ref'] = XLSX.utils.encode_range({
          s: { c: 0, r: 0 },
          e: { c: maxCol, r: maxRow }
        });
      }
      
      // Buffer oluştur
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      
      // Backend'e gönder
      const response = await axiosConfig.post('/admin/todos/save', {
        data: Array.from(new Uint8Array(excelBuffer))
      });

      if (response.data.fileSize > 0) {
        setHasChanges(false);
        showNotification('success', 'Değişiklikler başarıyla kaydedildi!');
        
        // Veriyi yeniden yükle
        setTimeout(() => {
          loadExcelData();
        }, 500);
      } else {
        throw new Error('Dosya boyutu 0 byte!');
      }
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      showNotification('error', 'Kaydetme sırasında bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // Excel dosyasını indir
  const handleDownload = async () => {
    try {
      const response = await axiosConfig.get('/admin/todos/download', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `todos-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('İndirme hatası:', error);
    }
  };

  // Excel dosyası yükle
  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Başlıkları al
          const range = XLSX.utils.decode_range(worksheet['!ref']);
          const headers = [];
          
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: 0 })];
            if (cell && cell.v) {
              headers.push({
                key: `col_${C}`,
                name: cell.v.toString(),
                width: '150px'
              });
            }
          }
          
          setColumns(headers);
          
          // Veriyi oku
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const formattedData = [];
          
          for (let i = 1; i < jsonData.length; i++) {
            const rowData = jsonData[i];
            if (rowData && rowData.length > 0) {
              const row = { _id: i - 1 };
              headers.forEach((header, index) => {
                row[header.key] = rowData[index] || '';
              });
              formattedData.push(row);
            }
          }
          
          setRows(formattedData);
          setCellColors({}); // Renkleri sıfırla
          setHasChanges(true);
          event.target.value = '';
        } catch (error) {
          console.error('Dosya okuma hatası:', error);
          showNotification('error', 'Dosya okuma sırasında bir hata oluştu!');
        } finally {
          setLoading(false);
        }
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Yükleme hatası:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Üst başlık */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h2 className="text-3xl font-bold text-white flex items-center">
              <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel Yapılacaklar Listesi
            </h2>
            <div className="flex gap-3 flex-wrap">
              <label className="bg-white/20 backdrop-blur text-white px-5 py-2.5 rounded-lg hover:bg-white/30 transition-all duration-200 flex items-center cursor-pointer">
                <FiUpload className="w-5 h-5 mr-2" />
                Excel Yükle
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
              
              <button
                className="bg-white/20 backdrop-blur text-white px-5 py-2.5 rounded-lg hover:bg-white/30 transition-all duration-200 flex items-center"
                onClick={handleDownload}
              >
                <FiDownload className="w-5 h-5 mr-2" />
                Excel İndir
              </button>
              
              <button
                className="bg-emerald-500 text-white px-5 py-2.5 rounded-lg hover:bg-emerald-600 transition-all duration-200 flex items-center shadow-lg disabled:opacity-50"
                onClick={handleSave}
                disabled={!hasChanges}
              >
                <FiSave className="w-5 h-5 mr-2" />
                Kaydet
              </button>
              
              <button
                className="bg-white text-purple-600 px-5 py-2.5 rounded-lg hover:bg-gray-100 transition-all duration-200 flex items-center shadow-lg"
                onClick={loadExcelData}
              >
                <FiRefreshCw className="w-5 h-5 mr-2" />
                Yenile
              </button>
            </div>
          </div>
        </div>

        {/* Bilgi mesajı */}
        {hasChanges && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-8 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Kaydedilmemiş değişiklikler var. Değişiklikleri kaydetmeyi unutmayın!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tablo kontrolleri */}
        <div className="px-8 py-4 bg-gray-50 border-b flex justify-between items-center">
          <div className="flex gap-3">
            <button
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all flex items-center text-sm disabled:opacity-50"
              onClick={handleAddRow}
              disabled={columns.length === 0}
            >
              <FiPlus className="w-4 h-4 mr-1" />
              Yeni Satır
            </button>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all flex items-center text-sm"
              onClick={() => setShowColumnModal(true)}
            >
              <FiPlus className="w-4 h-4 mr-1" />
              Sütun Ekle
            </button>
            <button
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all flex items-center text-sm disabled:opacity-50"
              onClick={handleDeleteRows}
              disabled={selectedRows.length === 0}
            >
              <FiTrash2 className="w-4 h-4 mr-1" />
              Seçili Sil ({selectedRows.length})
            </button>
            
            {/* Renk Paleti Butonu */}
            <div className="relative color-palette-container">
              <button
                className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-all flex items-center text-sm color-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowColorPalette(!showColorPalette);
                }}
              >
                <FiDroplet className="w-4 h-4 mr-1" />
                Renklendir
              </button>
              
              {/* Renk Paleti Dropdown */}
              {showColorPalette && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl p-3 z-10 min-w-[200px]">
                  <p className="text-xs text-gray-600 mb-2">Hücre seçip renklendirin</p>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {colorPalette.map((color, index) => (
                      <button
                        key={index}
                        className="w-10 h-10 rounded-lg border-2 hover:scale-110 transition-transform relative"
                        style={{ 
                          backgroundColor: color.value, 
                          borderColor: color.border 
                        }}
                        onClick={() => applyColor(color)}
                        title={color.name}
                      >
                        {selectedColor?.value === color.value && (
                          <FiCheck className="absolute inset-0 m-auto text-gray-700" />
                        )}
                      </button>
                    ))}
                  </div>
                  <button
                    className="w-full text-sm text-gray-600 hover:text-gray-800 mt-1"
                    onClick={() => applyColor(null)}
                  >
                    Rengi Kaldır
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              {selectedCells.length > 0 && (
                <span className="text-purple-600 font-medium mr-2">
                  {selectedCells.length} hücre seçili
                </span>
              )}
              {columns.length} sütun, {rows.length} satır
            </div>
          </div>
        </div>

        {/* Excel Tablosu */}
        <div className="p-8 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(rows.map(r => r._id));
                      } else {
                        setSelectedRows([]);
                      }
                    }}
                    checked={selectedRows.length === rows.length && rows.length > 0}
                  />
                </th>
                {columns.map(col => (
                  <th key={col.key} className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 relative group" style={{ minWidth: col.width }}>
                    <div className="flex items-center justify-between">
                      <span>{col.name}</span>
                      <button
                        onClick={() => handleDeleteColumn(col.key)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 text-red-500 hover:text-red-700"
                        title="Sütunu Sil"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isRowSelected = selectedRows.includes(row._id);
                return (
                  <tr key={row._id} className={`hover:bg-gray-50 ${isRowSelected ? 'bg-blue-50' : ''}`}>
                    <td className="border border-gray-300 px-4 py-2">
                      <input
                        type="checkbox"
                        checked={isRowSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRows([...selectedRows, row._id]);
                          } else {
                            setSelectedRows(selectedRows.filter(id => id !== row._id));
                          }
                        }}
                      />
                    </td>
                    {columns.map(col => {
                      const cellId = `${row._id}_${col.key}`;
                      const cellColor = cellColors[cellId];
                      const isCellSelected = selectedCells.includes(cellId);
                      
                      return (
                        <td 
                          key={col.key} 
                          className={`border border-gray-300 px-2 py-1 cursor-pointer ${isCellSelected ? 'ring-2 ring-purple-500' : ''}`}
                          style={{ backgroundColor: cellColor || 'transparent' }}
                          onClick={(e) => handleCellClick(row._id, col.key, e)}
                        >
                          <input
                            type="text"
                            value={row[col.key] || ''}
                            onChange={(e) => handleCellChange(row._id, col.key, e.target.value)}
                            className="w-full px-2 py-1 border-0 bg-transparent focus:ring-2 focus:ring-purple-500 rounded"
                            style={{ backgroundColor: 'transparent' }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                    <div className="max-w-md mx-auto">
                      <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium mb-2">Henüz veri yok</p>
                      <p className="text-sm text-gray-400 mb-4">Başlamak için sütun ekleyin veya Excel dosyası yükleyin</p>
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() => setShowColumnModal(true)}
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all text-sm"
                        >
                          <FiPlus className="w-4 h-4 inline mr-1" />
                          Sütun Ekle
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Kullanım Bilgisi */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Kullanım Kılavuzu</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <div>
                <p className="font-medium">Hücre Düzenleme</p>
                <p>Hücreye tıklayarak düzenleyin</p>
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <div>
                <p className="font-medium">Hücre Renklendirme</p>
                <p>Hücre seç → Renklendir butonuna tıkla</p>
                <p className="text-xs mt-1">Çoklu seçim: Ctrl+Tıkla</p>
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <div>
                <p className="font-medium">Excel İndir</p>
                <p>Mevcut veriyi Excel olarak indirin</p>
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <div>
                <p className="font-medium">Excel Yükle</p>
                <p>Bilgisayarınızdan Excel yükleyin</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sütun Ekleme Modal */}
      {showColumnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white">Yeni Sütun Ekle</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sütun Adı
              </label>
              <input 
                type="text"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Sütun adını girin..."
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddColumn();
                  }
                }}
                autoFocus
              />
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  onClick={() => {
                    setShowColumnModal(false);
                    setNewColumnName('');
                  }}
                >
                  İptal
                </button>
                <button
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all font-medium shadow-lg"
                  onClick={handleAddColumn}
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-5 right-5 z-50 animate-in fade-in slide-in-from-top-5 duration-300`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 
            notification.type === 'error' ? 'bg-red-500' : 
            notification.type === 'warning' ? 'bg-yellow-500' : 'bg-gray-500'
          } text-white min-w-[300px]`}>
            <div className="mr-3">
              {notification.type === 'success' && (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            <p className="font-medium">{notification.message}</p>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Onay Gerekiyor</h3>
                  <p className="text-gray-600 mt-1">{confirmDialog.message}</p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  onClick={() => closeConfirm(false)}
                >
                  İptal
                </button>
                <button
                  className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium shadow-lg"
                  onClick={() => closeConfirm(true)}
                >
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tailwind animation classes için style */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-from-top-5 {
          from { transform: translateY(-1.25rem); }
          to { transform: translateY(0); }
        }
        @keyframes zoom-in-95 {
          from { transform: scale(0.95); }
          to { transform: scale(1); }
        }
        .animate-in {
          animation-duration: 300ms;
          animation-fill-mode: both;
        }
        .fade-in {
          animation-name: fade-in;
        }
        .slide-in-from-top-5 {
          animation-name: slide-in-from-top-5;
        }
        .zoom-in-95 {
          animation-name: zoom-in-95;
        }
      `}</style>
    </div>
  );
};

export default TodoListPage;